/**
 * Formatting for the quote screens.
 *
 * Both helpers exist because of bugs staff reported on 2026-09-25:
 *  - "Valid Until" showed one day early. A calendar date (quote_expiry_date,
 *    delivery_date, or an expires_at carrying a date-only value) reaches the browser as
 *    "2026-09-04T00:00:00.000Z", because production runs UTC. Pushing that through
 *    `new Date(...).toLocaleDateString()` in America/Toronto lands on the 3rd.
 *  - The Total Value card read "$350336.17" — toFixed(2) has no thousands separator.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const UTC_MIDNIGHT = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?Z$/;

/**
 * Format a value that means a CALENDAR DAY, never a moment in time.
 *
 * A bare YYYY-MM-DD, and a timestamp that is exactly midnight UTC, are both treated as
 * the day they name: their digits are used directly, with no timezone conversion. Any
 * other timestamp is a real moment and is shown on its local day as before.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string} MM/DD/YYYY, or 'N/A'
 */
export const formatCalendarDate = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';

  // A Date that has already been parsed carries no hint that it began life as a calendar
  // day, so it used to fall through to toLocaleDateString() and show the day before west
  // of UTC. Read it back as its ISO string and apply the same rules.
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return 'N/A';
    return formatCalendarDate(value.toISOString());
  }

  if (typeof value === 'string') {
    const plain = value.match(DATE_ONLY) || value.match(UTC_MIDNIGHT);
    if (plain) {
      const [, y, m, d] = plain;
      return `${m}/${d}/${y}`;
    }
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

/**
 * Money, from integer cents, with thousands separators.
 * @param {number|string|null|undefined} cents
 * @returns {string} e.g. "$350,336.17"
 */
export const formatCentsCurrency = (cents) => {
  const n = Number(cents);
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    currencyDisplay: 'narrowSymbol'
  }).format((Number.isFinite(n) ? n : 0) / 100);
};

/**
 * The value an <input type="date"> needs: YYYY-MM-DD, taken from the day the stored value
 * NAMES. A DATE column arrives as midnight UTC, and feeding that straight to the input
 * either blanks it or shifts it a day; `toISOString()` on a local Date does the same in
 * reverse. Returns '' when there is nothing to show, so the input simply clears.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string} 'YYYY-MM-DD', or ''
 */
export const toDateInputValue = (value) => {
  if (value === null || value === undefined || value === '') return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === 'string') {
    const plain = value.match(DATE_ONLY) || value.match(UTC_MIDNIGHT);
    if (plain) {
      const [, y, m, d] = plain;
      return `${y}-${m}-${d}`;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 10);
  }

  return '';
};

/**
 * The calendar day a stored value means, as local { y, m, d } (m 1-12), or null. The rule
 * formatCalendarDate uses: a bare YYYY-MM-DD and a timestamp at exactly midnight UTC (how a
 * DATE column reaches the browser from a server running UTC) are the day their digits name;
 * any other timestamp is a moment, on its local day.
 */
const calendarDayOf = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return calendarDayOf(value.toISOString());
  }
  if (typeof value === 'string') {
    const plain = value.match(DATE_ONLY) || value.match(UTC_MIDNIGHT);
    if (plain) return { y: Number(plain[1]), m: Number(plain[2]), d: Number(plain[3]) };
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return { y: parsed.getFullYear(), m: parsed.getMonth() + 1, d: parsed.getDate() };
};

const dayNumber = ({ y, m, d }) => Date.UTC(y, m - 1, d) / 86400000;

/**
 * Days from today to the day `value` means, counted in calendar dates (local time, midnight to
 * midnight): 0 today, 1 tomorrow, -1 yesterday. Not elapsed hours / 24, which is a day off
 * across a clock change (a ten-day span over 2026-11-01 has 241 hours). null when there is no
 * date.
 *
 * @param {string|Date|null|undefined} value
 * @param {Date} [now]
 * @returns {number|null}
 */
export const calendarDaysUntil = (value, now = new Date()) => {
  const day = calendarDayOf(value);
  if (!day) return null;
  const today = { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
  return dayNumber(day) - dayNumber(today);
};

/**
 * The day `value` means as a local Date at midnight (for display next to a calendarDaysUntil
 * count), or null.
 */
export const calendarDateOf = (value) => {
  const day = calendarDayOf(value);
  return day ? new Date(day.y, day.m - 1, day.d) : null;
};
